import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api, { API, formatApiErrorDetail } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Check, Download, Loader2, RefreshCcw, Save, FileText, ChevronLeft,
} from "lucide-react";
import { DEFAULT_PROJECT, STEPS } from "./project/projectSchema";
import {
  StepBasic, StepCredit, StepFinancing, StepSchedule, StepPlot, StepInfra,
  StepProduction, StepNonAgri, StepFamily, StepLaudo, StepRecs, StepCashFlow,
} from "./project/StepComponents";

function mergeDeep(base, ext) {
  if (!ext) return base;
  const out = { ...base };
  for (const k of Object.keys(ext)) {
    if (
      ext[k] && typeof ext[k] === "object" && !Array.isArray(ext[k]) &&
      out[k] && typeof out[k] === "object" && !Array.isArray(out[k])
    ) {
      out[k] = mergeDeep(out[k], ext[k]);
    } else if (ext[k] !== undefined) {
      out[k] = ext[k];
    }
  }
  return out;
}

export default function ProjectEditorPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [project, setProject] = useState(null);
  const [data, setData] = useState(DEFAULT_PROJECT);
  const [name, setName] = useState("");
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data: proj } = await api.get(`/projects/${id}`);
        setProject(proj);
        setName(proj.name);
        setData(mergeDeep(DEFAULT_PROJECT, proj.data || {}));
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro");
        nav("/dashboard");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, nav]);

  const save = async (silent = false) => {
    setSaving(true);
    try {
      const { data: updated } = await api.put(`/projects/${id}`, { name, data });
      setProject(updated);
      if (!silent) toast.success("Projeto salvo");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro");
    } finally {
      setSaving(false);
    }
  };

  const recalc = async () => {
    setCalculating(true);
    try {
      const { data: calc } = await api.post("/calculate", { data });
      setData(mergeDeep(DEFAULT_PROJECT, calc));
      toast.success("Cálculos atualizados");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro");
    } finally {
      setCalculating(false);
    }
  };

  const downloadPdf = async () => {
    setGenerating(true);
    try {
      await save(true);
      const res = await fetch(`${API}/projects/${id}/pdf`, { credentials: "include" });
      if (!res.ok) throw new Error("Falha ao gerar PDF");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `PRONAF_${(name || "projeto").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF baixado");
    } catch (e) {
      toast.error(e.message || "Erro ao baixar PDF");
    } finally {
      setGenerating(false);
    }
  };

  const currentStep = STEPS[step];
  const stepContent = useMemo(() => {
    switch (currentStep?.id) {
      case "basic": return <StepBasic data={data} update={setData} />;
      case "credit": return <StepCredit data={data} update={setData} />;
      case "financing": return <StepFinancing data={data} update={setData} />;
      case "schedule": return <StepSchedule data={data} />;
      case "plot": return <StepPlot data={data} update={setData} />;
      case "infra": return <StepInfra data={data} update={setData} />;
      case "production": return <StepProduction data={data} update={setData} />;
      case "non_agri": return <StepNonAgri data={data} update={setData} />;
      case "family": return <StepFamily data={data} update={setData} />;
      case "laudo": return <StepLaudo data={data} update={setData} />;
      case "recs": return <StepRecs data={data} update={setData} />;
      case "cashflow": return <StepCashFlow data={data} update={setData} />;
      default: return null;
    }
  }, [currentStep, data]);

  if (loading) {
    return <AppShell><div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div></AppShell>;
  }

  return (
    <AppShell>
      <div className="mx-auto max-w-7xl px-6 py-8">
        <div className="flex items-center gap-3 mb-3">
          <Link to="/dashboard" className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1" data-testid="back-to-dashboard">
            <ChevronLeft className="h-4 w-4" /> Projetos
          </Link>
        </div>
        <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={() => save(true)}
                className="font-display font-bold text-xl border-0 bg-transparent focus-visible:ring-0 px-0 h-auto py-1"
                data-testid="project-name-input"
              />
            </div>
            <div className="text-xs text-muted-foreground ml-7">
              Projeto Simplificado PRONAF Grupo A - Investimento
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={recalc} disabled={calculating} data-testid="recalc-button">
              {calculating ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCcw className="h-4 w-4 mr-1" />}
              Recalcular
            </Button>
            <Button variant="outline" onClick={() => save(false)} disabled={saving} data-testid="save-button">
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
              Salvar
            </Button>
            <Button onClick={downloadPdf} disabled={generating} data-testid="download-pdf-button">
              {generating ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
              Gerar PDF
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
          {/* Vertical Stepper */}
          <aside className="lg:sticky lg:top-6 lg:self-start">
            <Card>
              <CardContent className="p-3">
                <ol className="space-y-1">
                  {STEPS.map((s, i) => {
                    const active = step === i;
                    const done = step > i;
                    return (
                      <li key={s.id}>
                        <button
                          type="button"
                          onClick={() => setStep(i)}
                          className={`w-full text-left flex items-center gap-3 px-3 py-2 rounded-md text-sm ${active ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}
                          data-testid={`step-nav-${s.id}`}
                        >
                          <span className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-semibold ${active ? "bg-accent text-accent-foreground" : done ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
                            {done ? <Check className="h-3 w-3" /> : i + 1}
                          </span>
                          <span className="font-medium">{s.label}</span>
                        </button>
                      </li>
                    );
                  })}
                </ol>
              </CardContent>
            </Card>
          </aside>

          {/* Form area */}
          <Card>
            <CardContent className="p-6 md:p-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">Passo {step + 1} de {STEPS.length}</div>
                  <h2 className="font-display text-2xl font-bold text-primary">{currentStep?.label}</h2>
                </div>
              </div>

              <div>{stepContent}</div>

              <div className="mt-8 pt-6 border-t flex items-center justify-between">
                <Button variant="outline" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} data-testid="prev-step-button">
                  <ArrowLeft className="h-4 w-4 mr-1" /> Anterior
                </Button>
                <div className="text-xs text-muted-foreground">{project ? `Última atualização: ${new Date(project.updated_at).toLocaleString("pt-BR")}` : ""}</div>
                {step < STEPS.length - 1 ? (
                  <Button onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))} data-testid="next-step-button">
                    Próximo <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                ) : (
                  <Button onClick={downloadPdf} disabled={generating} data-testid="finish-download-button">
                    {generating ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
                    Gerar e Baixar PDF
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AppShell>
  );
}
